import React, {createContext, useState, useContext, ReactNode, useCallback,} from "react";
import { GeneralInfoDropdownOptions, GeneralInformationData, PgpDecryptionData, SourceFileData, ValidationsData } from "../AddEditAutoImportModal/types/autoImportConfigContextTypes";
import { OutputDestination } from "../AddEditAutoImportModal/ModalFormSteps/OutputDestinations/types/outputDestinationsTypes";
import { AutoImportNotification } from "../AddEditAutoImportModal/ModalFormSteps/Notifications/types/notificationTypes";
import { AutoImportConfigurationData } from "../AutoImportConfigurations";

//form data from the AddEditAutoImportConfiguration Modal
//this will be expanded on as more form inputs are added to the modal
export interface AutoImportFormData {
  generalInformation: GeneralInformationData;
  sourceFile: SourceFileData;
  validations: ValidationsData;
  pgpDecryption: PgpDecryptionData;
  outputDestinations: OutputDestination[] | null;
  notifications: AutoImportNotification[] | null;
}

const defaultFormData: AutoImportFormData = {
  generalInformation: {
    client: undefined,
    productType: undefined,
    clientProductGuid: "",
    description: "",
    industryIdentifier: "",
    autoImportType: undefined,
    clientProductUser:undefined,
    templateMap: null,
    mapId: null,
  },
  sourceFile: {
    connection: undefined,
    additionalPath: "",
    fileSelectorMode: undefined,
    fileSelector: "",
    deleteSourceFile: true,
  },
  validations: {
    validateRepeatedFileName: true,
    validateRepeatedChecksum: true,
  },
  pgpDecryption: {
    usePgpDecryption: false,
    pgpPassPhrase: "",
    pgpPrivateKey: "",
  },
  outputDestinations: [],
  notifications: []
};


interface AutoImportFormContextProps {
  formData: AutoImportFormData;
  updateFormData: (newData: Partial<AutoImportFormData>) => void;
  updateStepData: <K extends keyof AutoImportFormData>( step: K, data: Partial<AutoImportFormData[K]>) => void;
  resetFormData: () => void;
  error: string | null;
  setError: (error: string | null) => void;
  generalInfoDropdownOptions: GeneralInfoDropdownOptions;
  updateGeneralInfoDropdownOptions: ( newOptions: Partial<GeneralInfoDropdownOptions>) => void;
  setFormData:(data : AutoImportConfigurationData | AutoImportFormData) => void
}


const AutoImportFormContext = createContext<AutoImportFormContextProps | undefined>(undefined);

interface AutoImportFormProviderProps {
  children: ReactNode;
}

//context used to track input data across the multi-step auto import config form
export const AutoImportFormProvider = ({children} : AutoImportFormProviderProps) => {
  const [formData, setFormData] = useState<AutoImportFormData>(defaultFormData);
  const [error, setError] = useState<string | null>(null);
  const [generalInfoDropdownOptions, setGeneralInfoDropdownOptions] =
    useState<GeneralInfoDropdownOptions>({
      //holds combobox data for "general info" form. Added here for better handling of dynamic combobox options
      activeClientList: [],
      clientProducts: [],
      autoImportTypes: [],
      templateMaps: [],
      clientProductUsers:[]
    });

  const updateFormData = useCallback((newData: Partial<AutoImportFormData>) => {
    setFormData((prevData) => ({
      ...prevData,
      ...newData,
    }));
  }, []);

  const updateStepData = useCallback(<K extends keyof AutoImportFormData>(step: K, data: Partial<AutoImportFormData[K]>) => {
    setFormData((prevData) => ({
      ...prevData,
      [step]: {
        ...prevData[step],
        ...data
      }
    }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormData(defaultFormData);
    setError(null);
  }, []);

  const updateGeneralInfoDropdownOptions = useCallback(
    (newOptions: Partial<GeneralInfoDropdownOptions>) => {
      setGeneralInfoDropdownOptions((prevOptions) => ({
        ...prevOptions,
        ...newOptions,
      }));
    },
    []
  );

  return (
    <AutoImportFormContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData,
        updateStepData,
        error,
        setError,
        generalInfoDropdownOptions,
        updateGeneralInfoDropdownOptions,
        setFormData
      }}
    >
      {children}
    </AutoImportFormContext.Provider>
  );
};

export const useAutoImportFormContext = (): AutoImportFormContextProps => {
  const context = useContext(AutoImportFormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
